/**
 * Browser-side auth state for the multi-user backend.
 *
 * Storage strategy:
 *   - Access token (15-min TTL) lives in module-scope memory only. A page
 *     reload throws it away — we recover by spending the refresh token.
 *     Keeping it out of localStorage means an XSS that exfiltrates storage
 *     gets at most the refresh token, not a token already accepted by the API.
 *   - Refresh token (14-day rolling TTL) lives in localStorage so logins
 *     survive reloads. The server rotates it on every /refresh; reuse of an
 *     old refresh token revokes the whole session family.
 *   - User profile (id / email / 2fa flags) is fetched from /api/auth/me on
 *     every successful login and refresh, then cached in module memory.
 *
 * The store uses `useSyncExternalStore` so React components re-render
 * deterministically when auth state changes (login / logout / refresh).
 * Cross-tab consistency: `storage` events (refresh token written by another
 * tab) trigger a re-read; broadcasts within the same tab fire a custom event.
 */

import { getApiUrl } from './settings';

// --- Types -----------------------------------------------------------------

export type Me = {
  id: string;
  email: string;
  email_verified: boolean;
  /** True until the user accepts the current app-wide Terms of Use; drives
   *  the blocking acceptance gate (see AppTermsGate). */
  app_terms_required: boolean;
  totp_enabled: boolean;
  is_admin: boolean;
  backup_codes_remaining: number;
  passkey_count: number;
  push_device_count: number;
  /** Raw user-set override; null when the user lets the default plan drive it. */
  monthly_budget: string | null;
  /**
   * Resolved budget the dashboard should use. Null when the user has neither
   * an override nor a default trajectory plan with non-zero monthly spend.
   */
  effective_monthly_budget: string | null;
  /** Where `effective_monthly_budget` came from. */
  monthly_budget_source: 'user' | 'plan' | null;
  /** Plan name when `monthly_budget_source === 'plan'`. */
  default_plan_name: string | null;
};

export type MfaFactor = 'totp' | 'push' | 'passkey';

export type MfaChallenge = {
  challenge_id: string;
  factors: MfaFactor[];
  expires_at: string;
  push_pending: boolean;
};

export type LoginResult =
  | { kind: 'ok' }
  | { kind: 'mfa_required'; mfa: MfaChallenge };

export type Passkey = {
  id: string;
  nickname: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

export type PushDevice = {
  id: string;
  nickname: string;
  platform: string;
  created_at: string;
  last_seen_at: string | null;
  revoked_at: string | null;
};

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type AuthState = {
  status: AuthStatus;
  me: Me | null;
};

export type CaptchaConfig = {
  provider: 'turnstile' | 'hcaptcha' | string;
  site_key: string;
};

export type OAuthConfig = {
  google_web_client_id: string | null;
  apple_web_service_id: string | null;
  google_enabled: boolean;
  apple_enabled: boolean;
};

export type PublicConfig = {
  open_registration: boolean;
  captcha: CaptchaConfig | null;
  oauth: OAuthConfig;
};

export type OAuthProvider = 'google' | 'apple';

// --- Storage keys ----------------------------------------------------------

const KEY_REFRESH = 'flowlet:refreshToken';
const STATE_EVENT = 'flowlet:authStateChanged';

// --- Module-scope state (memory only) -------------------------------------

let accessToken: string | null = null;
let me: Me | null = null;
let status: AuthStatus = 'loading';
// `useSyncExternalStore` does referential-equality checks on the snapshot —
// returning a fresh object every getSnapshot() call sends React into an
// infinite render loop. Cache one reference and only swap it when state
// actually changes (via publish()).
let cachedSnapshot: AuthState = { status, me };
const SERVER_SNAPSHOT: AuthState = Object.freeze({
  status: 'loading' as const,
  me: null,
});
// One in-flight /refresh promise at a time — protects against the obvious race
// where many components hit a 401 simultaneously and each tries to refresh.
let inFlightRefresh: Promise<boolean> | null = null;

// --- Internal helpers ------------------------------------------------------

function readRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(KEY_REFRESH);
}

function writeRefreshToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  if (token === null) localStorage.removeItem(KEY_REFRESH);
  else localStorage.setItem(KEY_REFRESH, token);
}

function publish(): void {
  cachedSnapshot = { status, me };
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(STATE_EVENT));
}

function setAuthenticated(token: string, refresh: string, profile: Me): void {
  accessToken = token;
  me = profile;
  status = 'authenticated';
  writeRefreshToken(refresh);
  publish();
}

function setUnauthenticated(): void {
  accessToken = null;
  me = null;
  status = 'unauthenticated';
  writeRefreshToken(null);
  publish();
}

// --- Public state-snapshot API (consumed by useSyncExternalStore) ---------

export function subscribe(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  // `storage` covers cross-tab updates; STATE_EVENT covers same-tab.
  window.addEventListener('storage', cb);
  window.addEventListener(STATE_EVENT, cb);
  return () => {
    window.removeEventListener('storage', cb);
    window.removeEventListener(STATE_EVENT, cb);
  };
}

export function getSnapshot(): AuthState {
  return cachedSnapshot;
}

export function getServerSnapshot(): AuthState {
  // SSR + first client render — auth status is unknown until we've tried
  // to spend the refresh token. Components show a neutral "loading" state.
  return SERVER_SNAPSHOT;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// --- HTTP helpers ----------------------------------------------------------

async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  return fetch(`${getApiUrl()}${path}`, { ...init, headers });
}

type TokenPair = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
};

// --- Auth actions ----------------------------------------------------------

export async function fetchPublicConfig(): Promise<PublicConfig> {
  const r = await apiFetch('/api/auth/public-config');
  if (!r.ok) throw new Error(await readError(r, 'public-config failed'));
  return r.json();
}

export async function register(input: {
  email: string;
  password: string;
  captchaToken?: string | null;
}): Promise<{ id: string }> {
  const { captchaToken, ...body } = input;
  const r = await apiFetch('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: captchaToken ? { 'X-Captcha-Token': captchaToken } : undefined,
  });
  if (!r.ok) throw new Error(await readError(r, 'register failed'));
  return r.json();
}

export async function verifyEmail(token: string): Promise<void> {
  const r = await apiFetch('/api/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  if (!r.ok) throw new Error(await readError(r, 'verify failed'));
}

export async function resendVerification(email: string): Promise<void> {
  // Backend deliberately returns 200 even for unknown emails — no enumeration.
  await apiFetch('/api/auth/verify-email/resend', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Stage 1 of the new two-step login. Returns ``{kind: 'ok'}`` if the user
 * has no 2FA (and is now authenticated) or ``{kind: 'mfa_required', mfa}``
 * with the challenge envelope. Stage 2 is one of:
 *
 *   - ``verifyTotp``      — user typed the 6-digit code
 *   - ``waitForPushApproval`` — user approves on their iOS device
 *   - ``loginWithPasskey``    — full WebAuthn ceremony
 */
export async function login(input: {
  email: string;
  password: string;
}): Promise<LoginResult> {
  const r = await apiFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (r.status === 401) {
    throw new Error('invalid credentials or account locked');
  }
  if (!r.ok) throw new Error(await readError(r, 'login failed'));

  const body = (await r.json()) as
    | { kind: 'ok'; tokens: TokenPair }
    | { kind: 'mfa_required'; mfa: MfaChallenge };

  if (body.kind === 'ok') {
    const profile = await fetchMe(body.tokens.access_token);
    setAuthenticated(body.tokens.access_token, body.tokens.refresh_token, profile);
    return { kind: 'ok' };
  }
  return { kind: 'mfa_required', mfa: body.mfa };
}

/**
 * Social login. ``provider`` is ``'google'`` or ``'apple'`` and ``id_token``
 * is the JWT minted by the provider's SDK. Same return envelope as
 * ``login`` — a 2FA-enabled account still has to clear its second factor.
 */
export async function loginWithOAuth(
  provider: OAuthProvider,
  id_token: string,
): Promise<LoginResult> {
  const r = await apiFetch(`/api/auth/oauth/${encodeURIComponent(provider)}`, {
    method: 'POST',
    body: JSON.stringify({ id_token }),
  });
  if (r.status === 401) {
    throw new Error(await readError(r, 'social sign-in rejected'));
  }
  if (!r.ok) throw new Error(await readError(r, `${provider} sign-in failed`));
  const body = (await r.json()) as
    | { kind: 'ok'; tokens: TokenPair }
    | { kind: 'mfa_required'; mfa: MfaChallenge };
  if (body.kind === 'ok') {
    const profile = await fetchMe(body.tokens.access_token);
    setAuthenticated(body.tokens.access_token, body.tokens.refresh_token, profile);
    return { kind: 'ok' };
  }
  return { kind: 'mfa_required', mfa: body.mfa };
}

async function consumeVerifyResponse(r: Response): Promise<void> {
  if (!r.ok) throw new Error(await readError(r, 'verify failed'));
  const tokens: TokenPair = await r.json();
  const profile = await fetchMe(tokens.access_token);
  setAuthenticated(tokens.access_token, tokens.refresh_token, profile);
}

export async function verifyTotp(input: {
  challenge_id: string;
  code: string;
}): Promise<void> {
  const r = await apiFetch('/api/auth/2fa/verify', {
    method: 'POST',
    body: JSON.stringify({
      challenge_id: input.challenge_id,
      factor: 'totp',
      totp_code: input.code,
    }),
  });
  await consumeVerifyResponse(r);
}

/**
 * Poll the challenge state until a push device flips it to ``approved``,
 * then exchange for tokens. Resolves to ``true`` on approval and ``false``
 * on denial / expiry. Throws on transport errors.
 */
export async function waitForPushApproval(
  challenge_id: string,
  opts: { signal?: AbortSignal; pollMs?: number } = {},
): Promise<boolean> {
  const pollMs = opts.pollMs ?? 1500;
  const deadline = Date.now() + 5 * 60 * 1000;
  while (Date.now() < deadline) {
    if (opts.signal?.aborted) return false;
    const r = await apiFetch(
      `/api/auth/2fa/challenge/${encodeURIComponent(challenge_id)}`,
    );
    if (!r.ok) throw new Error(await readError(r, 'poll failed'));
    const state = (await r.json()) as { status: string };
    if (state.status === 'approved') {
      const verifyR = await apiFetch('/api/auth/2fa/verify', {
        method: 'POST',
        body: JSON.stringify({
          challenge_id,
          factor: 'push',
        }),
      });
      await consumeVerifyResponse(verifyR);
      return true;
    }
    if (state.status === 'denied' || state.status === 'expired') {
      return false;
    }
    await new Promise((r) => setTimeout(r, pollMs));
  }
  return false;
}

// --- Passkey / WebAuthn ---------------------------------------------------

/**
 * Run the WebAuthn login ceremony bound to an existing MfaChallenge. The
 * server signs the challenge nonce into the request options, the browser
 * collects an assertion (FaceID / TouchID / security key), and we POST it
 * to /2fa/verify with factor=passkey.
 */
export async function loginWithPasskey(challenge_id: string): Promise<void> {
  const optsR = await apiFetch('/api/auth/2fa/passkey/login/options', {
    method: 'POST',
    body: JSON.stringify({ challenge_id }),
  });
  if (!optsR.ok) throw new Error(await readError(optsR, 'passkey options failed'));
  const { options_json } = (await optsR.json()) as { options_json: string };
  const options = JSON.parse(options_json);
  const credential = await navigator.credentials.get({
    publicKey: parsePublicKeyOptions(options),
  });
  if (!credential) throw new Error('passkey ceremony cancelled');
  const credentialJson = serializeCredentialAssertion(credential as PublicKeyCredential);
  const verifyR = await apiFetch('/api/auth/2fa/verify', {
    method: 'POST',
    body: JSON.stringify({
      challenge_id,
      factor: 'passkey',
      credential_json: credentialJson,
    }),
  });
  await consumeVerifyResponse(verifyR);
}

/**
 * Enroll a new Passkey for the authenticated user. The browser surfaces
 * the platform authenticator picker; on success we POST the attestation to
 * /2fa/passkey/register together with the registration token round-tripped
 * from /options.
 */
export async function registerPasskey(nickname: string): Promise<Passkey> {
  const optsR = await authedFetch('/api/auth/2fa/passkey/register/options', {
    method: 'POST',
  });
  if (!optsR.ok) throw new Error(await readError(optsR, 'passkey options failed'));
  const { options_json, registration_token } = (await optsR.json()) as {
    options_json: string;
    registration_token: string;
  };
  const options = JSON.parse(options_json);
  const credential = await navigator.credentials.create({
    publicKey: parsePublicKeyOptions(options),
  });
  if (!credential) throw new Error('passkey enrolment cancelled');
  const credentialJson = serializeCredentialAttestation(
    credential as PublicKeyCredential,
  );
  const r = await authedFetch('/api/auth/2fa/passkey/register', {
    method: 'POST',
    body: JSON.stringify({ registration_token, credential_json: credentialJson, nickname }),
  });
  if (!r.ok) throw new Error(await readError(r, 'passkey register failed'));
  if (accessToken) {
    me = await fetchMe(accessToken);
    publish();
  }
  return r.json();
}

export async function listPasskeys(): Promise<Passkey[]> {
  const r = await authedFetch('/api/auth/2fa/passkey/credentials');
  if (!r.ok) throw new Error(await readError(r, 'list passkeys failed'));
  return r.json();
}

export async function revokePasskey(id: string): Promise<void> {
  const r = await authedFetch(`/api/auth/2fa/passkey/credentials/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!r.ok) throw new Error(await readError(r, 'revoke passkey failed'));
  if (accessToken) {
    me = await fetchMe(accessToken);
    publish();
  }
}

// --- Push devices (read-only on web; enrolment happens in the iOS app) ----

export async function listPushDevices(): Promise<PushDevice[]> {
  const r = await authedFetch('/api/auth/2fa/push/devices');
  if (!r.ok) throw new Error(await readError(r, 'list push devices failed'));
  return r.json();
}

export async function revokePushDevice(id: string): Promise<void> {
  const r = await authedFetch(`/api/auth/2fa/push/devices/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!r.ok) throw new Error(await readError(r, 'revoke push device failed'));
  if (accessToken) {
    me = await fetchMe(accessToken);
    publish();
  }
}

// --- Disable 2FA via backup code + email confirmation --------------------

export async function startDisable2FA(input: {
  email: string;
  password: string;
  backup_code: string;
}): Promise<void> {
  // Always 200 — server returns silent ok regardless of validity.
  await apiFetch('/api/auth/2fa/disable/start', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function confirmDisable2FA(token: string): Promise<void> {
  const r = await apiFetch(
    `/api/auth/2fa/disable/confirm/${encodeURIComponent(token)}`,
    { method: 'POST' },
  );
  if (!r.ok) throw new Error(await readError(r, 'disable 2fa failed'));
}

export async function logout(): Promise<void> {
  const token = accessToken;
  setUnauthenticated();
  if (token) {
    // Best-effort — even if the server is unreachable, the client-side state
    // is already cleared.
    await apiFetch('/api/auth/logout', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => undefined);
  }
}

/**
 * Spend the stored refresh token to mint a fresh access + refresh pair.
 * Returns true on success, false if the user must log in again.
 *
 * Concurrent callers share one in-flight request — important for the
 * 401-interceptor path where many fetches can fail at once.
 */
export async function refresh(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    const refreshToken = readRefreshToken();
    if (!refreshToken) {
      setUnauthenticated();
      return false;
    }
    const r = await apiFetch('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!r.ok) {
      // 401 = expired / reuse-detected. Either way the family is gone — drop
      // local state so the user is sent back to /login.
      setUnauthenticated();
      return false;
    }
    const tokens: TokenPair = await r.json();
    try {
      const profile = await fetchMe(tokens.access_token);
      setAuthenticated(tokens.access_token, tokens.refresh_token, profile);
      return true;
    } catch {
      setUnauthenticated();
      return false;
    }
  })();
  try {
    return await inFlightRefresh;
  } finally {
    inFlightRefresh = null;
  }
}

/**
 * Run on app start — try to revive the session from the stored refresh
 * token. Until this resolves, `status` stays at `'loading'` so gated
 * components show a neutral spinner instead of flashing the login page.
 */
export async function bootstrap(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!readRefreshToken()) {
    setUnauthenticated();
    return;
  }
  await refresh();
}

async function fetchMe(token: string): Promise<Me> {
  const r = await apiFetch('/api/auth/me', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(await readError(r, '/me failed'));
  return r.json();
}

/**
 * Re-pull `/auth/me` and notify subscribers. Views call this when entering
 * surfaces that depend on server-derived fields (e.g. the dashboard's
 * spendable budget) so plan edits made elsewhere show up without a reload.
 * No-op when unauthenticated; silent on transient errors — a stale snapshot
 * is preferable to a thrown render path.
 */
export async function refreshMe(): Promise<void> {
  const token = accessToken;
  if (!token) return;
  try {
    me = await fetchMe(token);
    publish();
  } catch {
    // keep the previous snapshot
  }
}

/**
 * PATCH /api/auth/me — currently only updates `monthly_budget`.
 *
 * Pass `null` to clear the stored value (server interprets this as
 * `clear_monthly_budget=true`). Updates the cached profile so any
 * subscriber re-renders without an extra round-trip.
 */
export async function updateMonthlyBudget(value: number | null): Promise<Me> {
  const token = accessToken;
  if (!token) throw new Error('not authenticated');
  const body =
    value === null
      ? { clear_monthly_budget: true, monthly_budget: null }
      : { clear_monthly_budget: false, monthly_budget: value };
  const r = await apiFetch('/api/auth/me', {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await readError(r, '/me update failed'));
  const profile = (await r.json()) as Me;
  me = profile;
  publish();
  return profile;
}

// --- Password reset --------------------------------------------------------

export async function requestPasswordReset(email: string): Promise<void> {
  // Always 200 from the backend — silent for unknown emails.
  await apiFetch('/api/auth/reset-password/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function confirmPasswordReset(input: {
  token: string;
  new_password: string;
}): Promise<void> {
  const r = await apiFetch('/api/auth/reset-password/confirm', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error(await readError(r, 'reset failed'));
}

// --- Account recovery (lost 2FA + lost backup codes) ---------------------

/**
 * Step 1 — public; takes email; backend silently accepts unknown emails so
 * we never leak which addresses are registered. The legitimate user gets a
 * cancel-link email immediately; the user has to wait 7 days for the
 * complete-link email.
 */
export async function startAccountRecovery(
  email: string,
  captchaToken?: string | null,
): Promise<void> {
  await apiFetch('/api/auth/recovery/start', {
    method: 'POST',
    body: JSON.stringify({ email }),
    headers: captchaToken ? { 'X-Captcha-Token': captchaToken } : undefined,
  });
}

/**
 * Step 2a — public; legitimate user clicks the cancel link in their inbox to
 * abort a malicious recovery attempt. Backend returns 200 silently for
 * unknown / already-consumed tokens (no enumeration).
 */
export async function cancelAccountRecovery(token: string): Promise<void> {
  await apiFetch(`/api/auth/recovery/cancel/${encodeURIComponent(token)}`, {
    method: 'POST',
  });
}

/**
 * Step 2b — public; user clicks the complete link sent after the cooldown
 * elapsed. Sets a new password and drops their TOTP enrolment in one shot;
 * existing sessions and backup codes are also burned server-side.
 */
export async function completeAccountRecovery(input: {
  token: string;
  new_password: string;
}): Promise<void> {
  const r = await apiFetch(
    `/api/auth/recovery/complete/${encodeURIComponent(input.token)}`,
    {
      method: 'POST',
      body: JSON.stringify({ new_password: input.new_password }),
    },
  );
  if (!r.ok) throw new Error(await readError(r, 'recovery failed'));
}

// --- 2FA -------------------------------------------------------------------

export type TotpSetup = {
  secret: string;
  provisioning_uri: string;
  manual_entry: string;
};

export async function setupTotp(): Promise<TotpSetup> {
  const r = await authedFetch('/api/auth/2fa/setup', { method: 'POST' });
  if (!r.ok) throw new Error(await readError(r, '2fa setup failed'));
  return r.json();
}

export async function enableTotp(code: string): Promise<string[]> {
  const r = await authedFetch('/api/auth/2fa/enable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  if (!r.ok) throw new Error(await readError(r, '2fa enable failed'));
  const body = (await r.json()) as { backup_codes: string[] };
  // Re-fetch /me so `totp_enabled` flips in the cached profile.
  if (accessToken) {
    me = await fetchMe(accessToken);
    publish();
  }
  return body.backup_codes;
}

/**
 * Generate a fresh set of backup codes, invalidating any previous set.
 * Returns the plaintext codes — show them once, never store.
 */
export async function rotateBackupCodes(): Promise<string[]> {
  const r = await authedFetch('/api/auth/2fa/backup-codes', { method: 'POST' });
  if (!r.ok) throw new Error(await readError(r, 'rotate backup codes failed'));
  const body = (await r.json()) as { backup_codes: string[] };
  if (accessToken) {
    me = await fetchMe(accessToken);
    publish();
  }
  return body.backup_codes;
}

// --- Auth-bearing fetch with 401 → refresh → retry ------------------------

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  if (!accessToken) {
    if (!(await refresh())) throw new Error('not authenticated');
  }
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('Authorization', `Bearer ${accessToken}`);
  let r = await fetch(`${getApiUrl()}${path}`, { ...init, headers });
  if (r.status === 401) {
    if (await refresh()) {
      headers.set('Authorization', `Bearer ${accessToken}`);
      r = await fetch(`${getApiUrl()}${path}`, { ...init, headers });
    }
  }
  return r;
}

// --- Misc utilities --------------------------------------------------------

async function safeJson(r: Response): Promise<unknown> {
  try {
    return await r.json();
  } catch {
    return null;
  }
}

async function readError(r: Response, fallback: string): Promise<string> {
  const body = await safeJson(r);
  if (body && typeof body === 'object' && body !== null && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === 'string') return detail;
    return JSON.stringify(detail);
  }
  return `${fallback} (HTTP ${r.status})`;
}

// --- WebAuthn helpers -----------------------------------------------------
//
// The server emits the canonical WebAuthn JSON shape (`challenge` and
// `user.id` are base64url strings). The browser API expects ArrayBuffers,
// so we decode there; on the way back we encode the assertion fields the
// server's `webauthn` library expects.

function b64urlToBuf(value: string): ArrayBuffer {
  // base64url → base64 (re-pad to multiple of 4 since the server strips
  // trailing '=' chars).
  const pad = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + pad).replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

type ServerCredentialDescriptor = { id: string; type: string; transports?: string[] };
type ServerPublicKeyOptions = {
  challenge: string;
  rp?: unknown;
  user?: { id: string; name: string; displayName: string };
  pubKeyCredParams?: unknown;
  excludeCredentials?: ServerCredentialDescriptor[];
  allowCredentials?: ServerCredentialDescriptor[];
  authenticatorSelection?: unknown;
  attestation?: unknown;
  timeout?: number;
  userVerification?: unknown;
  rpId?: string;
};

function parsePublicKeyOptions(
  raw: ServerPublicKeyOptions,
): PublicKeyCredentialCreationOptions & PublicKeyCredentialRequestOptions {
  const out = { ...raw } as Record<string, unknown>;
  out.challenge = b64urlToBuf(raw.challenge);
  if (raw.user) {
    out.user = { ...raw.user, id: b64urlToBuf(raw.user.id) };
  }
  if (raw.excludeCredentials) {
    out.excludeCredentials = raw.excludeCredentials.map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    }));
  }
  if (raw.allowCredentials) {
    out.allowCredentials = raw.allowCredentials.map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    }));
  }
  // The shape varies between create() and get() — the union we return lets
  // callers pass it to either ceremony. Cast through `unknown` since
  // TypeScript can't see that `out` carries all the required fields when
  // the input does (the server only emits well-formed options for its
  // own ceremony types).
  return out as unknown as PublicKeyCredentialCreationOptions &
    PublicKeyCredentialRequestOptions;
}

function serializeCredentialAttestation(cred: PublicKeyCredential): string {
  const resp = cred.response as AuthenticatorAttestationResponse;
  return JSON.stringify({
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      attestationObject: bufToB64url(resp.attestationObject),
      transports: typeof resp.getTransports === 'function' ? resp.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: cred.authenticatorAttachment ?? null,
  });
}

function serializeCredentialAssertion(cred: PublicKeyCredential): string {
  const resp = cred.response as AuthenticatorAssertionResponse;
  return JSON.stringify({
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      authenticatorData: bufToB64url(resp.authenticatorData),
      signature: bufToB64url(resp.signature),
      userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: cred.authenticatorAttachment ?? null,
  });
}
